import React, { useState, useEffect, useCallback } from 'react';
import { Modal } from 'react-bootstrap';
import { Client } from '@xmtp/browser-sdk';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSpinner, faDesktop, faTrash, faTriangleExclamation } from '@fortawesome/free-solid-svg-icons';
import { XMTP_ENV } from '../services/xmtpClient';

interface InstallationInfo {
  id: string;
  bytes: Uint8Array;
  createdAt: Date;
  isCurrent: boolean;
}

interface InstallationManagerModalProps {
  show: boolean;
  onHide: () => void;
  pendingClient: any;
  pendingInboxId: string | null;
  onRevokeInstallations: (installationBytes: Uint8Array[], revokeAll: boolean) => Promise<void>;
  onRevocationComplete: () => void;
}

const InstallationManagerModal: React.FC<InstallationManagerModalProps> = ({
  show,
  onHide,
  pendingClient,
  pendingInboxId,
  onRevokeInstallations,
  onRevocationComplete,
}) => {
  const { t } = useTranslation();
  const [installations, setInstallations] = useState<InstallationInfo[]>([]);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [isLoading, setIsLoading] = useState(false);
  const [isRevoking, setIsRevoking] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!show || (!pendingClient && !pendingInboxId)) return;
    let cancelled = false;

    async function fetchInstallations() {
      setIsLoading(true);
      setError('');
      setSelectedIds(new Set());
      try {
        let inboxState: any;
        let currentInstallationId: string | undefined;

        if (pendingClient) {
          // Use client instance method
          inboxState = await pendingClient.preferences.inboxState(true);
          currentInstallationId = pendingClient.installationId;
        } else if (pendingInboxId) {
          // No client available — use static SDK method
          const states = await (Client as any).inboxStateFromInboxIds(
            [pendingInboxId],
            XMTP_ENV,
          );
          inboxState = states?.[0];
        }

        if (!inboxState) {
          if (!cancelled) setError('Failed to load installations');
          return;
        }

        const items: InstallationInfo[] = (inboxState.installations || []).map(
          (inst: any) => {
            const createdAt = inst.clientTimestampNs
              ? new Date(Number(BigInt(inst.clientTimestampNs) / BigInt(1_000_000)))
              : new Date(0);

            return {
              id: inst.id || '',
              bytes: inst.bytes,
              createdAt,
              isCurrent: currentInstallationId ? inst.id === currentInstallationId : false,
            };
          }
        );

        // Sort: current first (if any), then by date descending
        items.sort((a, b) => {
          if (a.isCurrent) return -1;
          if (b.isCurrent) return 1;
          return b.createdAt.getTime() - a.createdAt.getTime();
        });

        if (!cancelled) setInstallations(items);
      } catch (err: any) {
        console.error('Failed to fetch inbox state:', err);
        if (!cancelled) setError('Failed to load installations');
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    }

    fetchInstallations();
    return () => { cancelled = true; };
  }, [show, pendingClient, pendingInboxId]);

  const toggleSelect = useCallback((id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const toggleSelectAll = useCallback(() => {
    const otherIds = installations.filter((i) => !i.isCurrent).map((i) => i.id);
    setSelectedIds((prev) => {
      if (prev.size === otherIds.length) return new Set();
      return new Set(otherIds);
    });
  }, [installations]);

  const handleRevoke = useCallback(async () => {
    if (selectedIds.size === 0) return;
    setIsRevoking(true);
    setError('');

    try {
      const toRevoke = installations.filter(
        (inst) => selectedIds.has(inst.id) && !inst.isCurrent
      );

      if (toRevoke.length === 0) {
        setError('No installations selected for revocation');
        setIsRevoking(false);
        return;
      }

      const otherInstallations = installations.filter((inst) => !inst.isCurrent);
      const revokeAll = toRevoke.length === otherInstallations.length;
      const bytesToRevoke = toRevoke.map((inst) => inst.bytes);

      await onRevokeInstallations(bytesToRevoke, revokeAll);

      toast.success(`Revoked ${toRevoke.length} installation(s)`);
      onRevocationComplete();
    } catch (err: any) {
      console.error('Revocation failed:', err);
      setError(err.message || 'Failed to revoke installations');
    } finally {
      setIsRevoking(false);
    }
  }, [selectedIds, installations, onRevokeInstallations, onRevocationComplete]);

  const otherCount = installations.filter((i) => !i.isCurrent).length;
  const allOthersSelected = selectedIds.size > 0 && selectedIds.size === otherCount;

  return (
    <Modal show={show} onHide={onHide} centered className="chat-modal" size="lg">
      <Modal.Header closeButton>
        <Modal.Title>
          {t('chat.installationManager', 'Manage Encryption Keys')}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <p className="chat-modal__description">
          {t(
            'chat.installationLimitDesc',
            "You have reached the maximum of 10 encryption keys. Each browser or device uses a unique key. Select the old keys you no longer use and revoke them so this device can be registered."
          )}
        </p>

        {isLoading ? (
          <div style={{ textAlign: 'center', padding: '20px' }}>
            <FontAwesomeIcon icon={faSpinner} spin />{' '}
            {t('chat.loadingInstallations', 'Loading installations...')}
          </div>
        ) : (
          <>
            <div className="chat-installation-list__header">
              <label className="chat-modal__label" style={{ marginBottom: 0 }}>
                {t('chat.installations', 'Registered Keys')} ({installations.length}/10)
              </label>
              {otherCount > 0 && (
                <button
                  className="chat-btn chat-btn--secondary chat-btn--sm"
                  onClick={toggleSelectAll}
                >
                  {allOthersSelected
                    ? t('chat.deselectAll', 'Deselect All')
                    : t('chat.selectAllOthers', 'Select All Others')}
                </button>
              )}
            </div>

            <div className="chat-installation-list">
              {installations.map((inst) => (
                <div
                  key={inst.id}
                  className={`chat-installation-item${
                    inst.isCurrent ? ' chat-installation-item--current' : ''
                  }${
                    selectedIds.has(inst.id)
                      ? ' chat-installation-item--selected'
                      : ''
                  }`}
                  onClick={() => {
                    if (!inst.isCurrent) toggleSelect(inst.id);
                  }}
                >
                  <div className="chat-installation-item__checkbox">
                    {inst.isCurrent ? (
                      <FontAwesomeIcon
                        icon={faDesktop}
                        style={{ color: '#037FFF' }}
                      />
                    ) : (
                      <input
                        type="checkbox"
                        checked={selectedIds.has(inst.id)}
                        onChange={() => toggleSelect(inst.id)}
                        onClick={(e) => e.stopPropagation()}
                      />
                    )}
                  </div>
                  <div className="chat-installation-item__info">
                    <span className="chat-installation-item__id">
                      {inst.id.slice(0, 8)}...{inst.id.slice(-8)}
                    </span>
                    {inst.isCurrent && (
                      <span className="chat-installation-item__badge">
                        {t('chat.currentDevice', 'This Device')}
                      </span>
                    )}
                    <span className="chat-installation-item__date">
                      {inst.createdAt.getTime() > 0
                        ? inst.createdAt.toLocaleDateString(undefined, {
                            month: 'short',
                            day: 'numeric',
                            year: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit',
                          })
                        : t('chat.unknownDate', 'Unknown date')}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}

        {selectedIds.size > 0 && (
          <div className="chat-modal__warning">
            <FontAwesomeIcon icon={faTriangleExclamation} />{' '}
            {t(
              'chat.revokeWarning',
              'After revoking keys and registering this device, previous message history will not be available. Only new messages will appear in your conversations.'
            )}
          </div>
        )}

        {error && <p className="chat-modal__error">{error}</p>}
      </Modal.Body>
      <Modal.Footer>
        <button className="chat-btn chat-btn--secondary" onClick={onHide}>
          {t('chat.cancel', 'Cancel')}
        </button>
        <button
          className="chat-btn chat-btn--primary chat-btn--danger"
          onClick={handleRevoke}
          disabled={isRevoking || selectedIds.size === 0}
        >
          {isRevoking ? (
            <>
              <FontAwesomeIcon icon={faSpinner} spin />{' '}
              {t('chat.revoking', 'Revoking...')}
            </>
          ) : (
            <>
              <FontAwesomeIcon icon={faTrash} />{' '}
              {t('chat.revokeSelected', 'Revoke Selected')} ({selectedIds.size})
            </>
          )}
        </button>
      </Modal.Footer>
    </Modal>
  );
};

export default InstallationManagerModal;
